import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { EventType } from 'src/app/constants/events-type';
import { EventContent } from 'src/app/core/services/event-manager/models/event-content';
import { EventManagerService } from '../../../core/services/event-manager/event-manager.service';
import { Alert } from '../../alert/types/alert';
import { AutoCompleteComponent } from '../auto-complete.component';
import { AutoCompleteItem } from '../interfaces/autocomplete-item';

@Component({
    selector: 'app-auto-complete-list-group',
    templateUrl: './auto-complete-list-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AutoCompleteListGroupComponent extends AutoCompleteComponent {
  @ViewChild(NgSelectComponent) ngSelectComponent: NgSelectComponent | undefined;

  selectedItems: AutoCompleteItem[] = [];
  @Input() set selectedItemsData(selectedItems: AutoCompleteItem[]) {
    if (!selectedItems) {
      this.selectedItems = [];
      return;
    }

    this.selectedItems = [...selectedItems];
  }
  /**
   * Uses for external binding of selected items,
   * for example from file
   */
  @Input() set externalSelectedItemsData(selectedItems: AutoCompleteItem[]) {
    if (!selectedItems || selectedItems.length === 0) {
      return;
    }

    this.emitSelectChange(selectedItems);
  }

  @Input() set removeSelectedItem(item: AutoCompleteItem | undefined) {
    if (!item) {
      return;
    }

    this.remove(item);
  }

  @Input() placeholder = 'SEARCH';
  @Input() shouldShowSelectedItemsList = false;
  @Input() shouldShowDefaultMessage = false;
  @Input() defaultMessageContent = 'ADD_ITEMS';
  @Input() notAbleToEditListGroupItemLabel = 'NOT_ABLE_TO_EDIT';
  @Input() inputTitle: string | undefined;
  @Input() shouldBeAbleToEditListGroupItem = true;
  @Input() customListClass = '';
  @Input() rosterStudents?: any | undefined;
  @Input() inputId: string | undefined;

  @Output() itemClicked: EventEmitter<AutoCompleteItem> = new EventEmitter<AutoCompleteItem>();

  get effectiveInputId(): string {
    if (this.inputId) {
      return this.inputId;
    }
    const raw = (this.placeholder || 'list-group')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `ac-lg-${raw || 'select'}`.slice(0, 95);
  }

  constructor(
    private eventManagerService: EventManagerService,
    liveAnnouncer: LiveAnnouncer,
    translate: TranslateService
  ) {
    super(liveAnnouncer, translate);
  }

  /** WCAG 4.1.2 / 2.5.3 — icon-only remove must name who is removed (Deque: button-name-inaccessible). */
  removeItemAriaLabel(item: AutoCompleteItem): string {
    const title = [item.extraLabels?.firstLabel, item.extraLabels?.secondLabel].filter(Boolean).join(' ').trim();
    const label = title
      ? this.translate.instant('A11Y_ROW_ACTION_REMOVE', { title })
      : this.translate.instant('REMOVE');
    const trimmed = label?.trim();
    if (trimmed) {
      return trimmed;
    }
    return title ? `Remove: ${title}` : 'Remove';
  }

  onRemove(item: AutoCompleteItem): void {
    this.remove(item);
  }

  onItemClick(item: AutoCompleteItem): void {
    this.itemClicked.emit(item);
  }

  remove(item: AutoCompleteItem): void {
    if (!item) {
      return;
    }

    // Removes item from the list below the autocomplete input
    this.selectedItems = this.selectedItems.filter(selectedItem => selectedItem.value !== item.value);
    this.selectChange.emit(this.selectedItems);
  }

  onChange(item: AutoCompleteItem): void {
    this.emitSelectChange([item]);
    this.ngSelectComponent?.handleClearClick();
  }

  emitSelectChange(selectedItems: AutoCompleteItem[]): void {
    selectedItems.forEach(selectedItem => {
      if (!selectedItem) {
        return;
      }

      if (this.isThereDublicate(selectedItem)) {
        return;
      }

      this.selectedItems.push(selectedItem);
      this.selectChange.emit(this.selectedItems);
    });
  }

  private isThereDublicate(selectedItem: AutoCompleteItem): boolean {
    const targetSelectedItem = this.selectedItems.find(item => item.value === selectedItem.value);
    
    const targetSelectedItemInRoster = this.rosterStudents && this.rosterStudents.find((item:any) => item.uuid === selectedItem.value);

    if(!targetSelectedItem && !targetSelectedItemInRoster){
      return false;
    }

    const alert: Alert = {
      type: 'danger',
      dismissOnTimeout: 0,
      translateKey: 'USER_HAVE_ALREADY_BEEN_ADDED',
      interpolateParams: { value: selectedItem.extraLabels.value }
    };
    this.eventManagerService.broadcast(new EventContent(EventType.AlertMessage, alert));

    return true;
  }
}
